/**
 * AppErrorBoundary — top-level React error boundary.
 *
 * Phase-6 W4 T4.4 (`P6-BL-APP-ERROR-BOUNDARY`). Catches uncaught
 * rendering errors anywhere in the SPA tree and surfaces a warm-themed
 * fallback panel with a «Перезагрузить» CTA. Sits OUTSIDE the
 * `RouterProvider` so it also catches errors in the router's own
 * render path (data-router error boundaries inside `RouterProvider`
 * cover route-level errors; this one is the final catch-all).
 *
 * Why a class component (not `react-error-boundary`)?
 *   - The library is not a project dependency (apps/web/package.json),
 *     and adding it for a single boundary is unjustified weight.
 *   - The React class API is stable and self-documenting; we already
 *     use the same shape for `RouteErrorBoundary`.
 *
 * Security:
 *   - We NEVER render the raw `Error.stack` or message body — a leaked
 *     stack trace is a known XSS-precursor (paths reveal directory
 *     layout, exception messages can echo user input). The fallback
 *     shows a generic ru-RU notice; the actual error goes to
 *     `console.error` for live-VPS replay only.
 *
 * NOT Sentry: M2-scope (Q34 / S-DA cluster) explicitly defers any
 * external telemetry. The boundary's job here is graceful degradation,
 * not error reporting.
 */
import * as React from 'react';

import { Button } from '@/components/ui/button';

interface State {
  /** Latched error — non-null once an error has been caught. */
  error: Error | null;
}

interface Props {
  children: React.ReactNode;
}

export class AppErrorBoundary extends React.Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Console-only sink. Phase-6 explicitly excludes Sentry / external
    // telemetry per M2 scope. The componentStack helps engineering
    // when reproducing from a user-supplied browser console log.
    console.error('[AppErrorBoundary] uncaught render error:', error, errorInfo);
  }

  handleReload = (): void => {
    // Hard reload — fresh module graph + new chunk manifest. The
    // boundary's whole-tree latching means a `setState({error: null})`
    // would re-render the same failing subtree on the same module
    // graph, which is almost always the wrong UX.
    if (typeof window !== 'undefined') {
      window.location.reload();
    } else {
      this.setState({ error: null });
    }
  };

  override render(): React.ReactNode {
    if (!this.state.error) {
      return this.props.children;
    }
    return (
      <div
        role="alert"
        aria-live="assertive"
        data-testid="app-error-boundary"
        className="grid min-h-screen place-items-center bg-bg p-6"
      >
        <div
          className="flex w-full max-w-md flex-col gap-4 rounded-lg border border-line bg-card p-6 text-center shadow-3"
          data-testid="app-error-boundary-card"
        >
          <h1 className="text-lg font-semibold text-ink" data-testid="app-error-boundary-title">
            Что-то пошло не так
          </h1>
          <p className="text-sm text-muted" data-testid="app-error-boundary-message">
            Произошла непредвиденная ошибка. Попробуйте перезагрузить страницу — если проблема
            повторится, сообщите администратору.
          </p>
          <div className="flex justify-center">
            <Button
              type="button"
              variant="default"
              onClick={this.handleReload}
              data-testid="app-error-boundary-reload"
            >
              Перезагрузить
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
